"use client"

import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { PauseIcon, PlayIcon } from 'lucide-react'
import WaveSurfer from 'wavesurfer.js'

type AudioPlayerProps = {
  src: string
  className?: string
}

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function AudioPlayer({ src, className }: AudioPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const wsRef = useRef<WaveSurfer | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isReady, setIsReady] = useState(false)

  const resolveColors = useCallback(() => {
    const el = containerRef.current
    if (!el) return { wave: '#888', progress: '#6dc6fc' }
    const s = getComputedStyle(el)
    return {
      wave: s.getPropertyValue('--muted-foreground').trim() || '#888',
      progress: s.getPropertyValue('--primary').trim() || '#6dc6fc',
    }
  }, [])

  useEffect(() => {
    if (!containerRef.current) return

    const { wave, progress } = resolveColors()

    const ws = WaveSurfer.create({
      container: containerRef.current,
      height: 40,
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      cursorWidth: 0,
      waveColor: wave,
      progressColor: progress,
      url: src,
      normalize: true,
      dragToSeek: true,
    })

    wsRef.current = ws

    ws.on('ready', () => {
      setDuration(ws.getDuration())
      setIsReady(true)
    })
    ws.on('timeupdate', setCurrentTime)
    ws.on('play', () => setIsPlaying(true))
    ws.on('pause', () => setIsPlaying(false))
    ws.on('finish', () => setIsPlaying(false))

    const mo = new MutationObserver(() => {
      if (!wsRef.current) return
      const c = resolveColors()
      wsRef.current.setOptions({ waveColor: c.wave, progressColor: c.progress })
    })
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] })

    return () => {
      mo.disconnect()
      ws.destroy()
      wsRef.current = null
      setIsPlaying(false)
      setCurrentTime(0)
      setDuration(0)
      setIsReady(false)
    }
  }, [src, resolveColors])

  const handleToggle = useCallback(() => {
    const ws = wsRef.current
    if (!ws) return
    ws.playPause()
  }, [])

  return (
    <div className={cn('flex w-full items-center gap-3', className)}>
      <Button
        size="icon"
        variant="ghost"
        onClick={handleToggle}
        disabled={!isReady}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        className="shrink-0"
      >
        {isPlaying ? <PauseIcon size={18} /> : <PlayIcon size={18} />}
      </Button>
      <div className="flex-1 min-w-0 nodrag nowheel" ref={containerRef} />
      <div className="tabular-nums text-xs text-muted-foreground shrink-0">
        {formatTime(currentTime)} / {formatTime(duration)}
      </div>
    </div>
  )
}
